import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { getStroke } from 'perfect-freehand';
import type { BoardDoc } from '../../doc/BoardDoc';
import { distance, type Point } from '../../doc/geometry';
import type { DrawTool, Geometry } from '../../doc/schema';
import { commitTransient } from '../actions';
import { expandGroups } from '../arrange';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { endErase, eraseTo, startErase, type EraseGesture } from '../eraser';
import { filterSelectable, isInk, lassoStrokes, strokeAt, strokeHit } from '../ink';
import { HIGHLIGHTER_ALPHA, outlineToPath, strokeOptions } from '../items/StrokeItem';
import { sampleColor } from '../sampleColor';
import { setInkStyle, setPenMode, toolSettings } from '../toolSettings';
import { screenToWorld, worldToScreen } from './camera';

/** Time after a pen touch during which finger input is treated as a resting palm. */
export const PALM_WINDOW_MS = 1500;
let lastPenContact = 0;
export const notePenContact = (): void => {
  lastPenContact = Date.now();
};
export const penRecentlyUsed = (): boolean => Date.now() - lastPenContact < PALM_WINDOW_MS;

/** Should this pointer be ignored as a palm (or per the pen-only setting)? */
export function isRejectedTouch(pointerType: string): boolean {
  if (pointerType === 'pen') {
    notePenContact();
    return false;
  }
  if (pointerType !== 'touch') return false;
  return toolSettings.getState().penOnly || penRecentlyUsed();
}

/** Screen pixels within which a press counts as "on" a stroke (lasso tap and move). */
const HIT_PX = 8;
const TAP_PX = 4;
/** Holding this still for this long at the end of a line straightens it. */
const HOLD_MS = 600;
const HOLD_SLOP_PX = 4;
/** Shorter lines never straighten (a pause before drawing is not a line). */
const MIN_LINE_PX = 12;
/** Shift-drawn lines snap to multiples of this angle. */
const ANGLE_STEP = Math.PI / 12;

/** Undo merge windows a gesture is holding open, restored when it ends. */
const heldTimeouts = new WeakMap<BoardDoc['undo'], number>();

/** One gesture, one undo step: nothing before it merges in, and pauses do not split it. */
function openUndoStep(board: BoardDoc): void {
  const undo = board.undo;
  undo.stopCapturing();
  if (!heldTimeouts.has(undo)) heldTimeouts.set(undo, undo.captureTimeout);
  undo.captureTimeout = Infinity;
}

/** Ends the gesture's undo step: whatever changes next starts a new one. */
function closeUndoStep(board: BoardDoc): void {
  const undo = board.undo;
  const timeout = heldTimeouts.get(undo);
  if (timeout !== undefined) {
    undo.captureTimeout = timeout;
    heldTimeouts.delete(undo);
  }
  undo.stopCapturing();
}

/**
 * Screen-space canvas that captures ink while the draw tool is active. The stroke is drawn
 * immediately on the canvas (no React on the hot path); on release it becomes a stroke item on
 * the ink layer of its tool. The same canvas runs the eraser, the colour picker (its mode, or
 * Alt with any other), and in lasso mode draws the selection loop, selects the strokes it
 * touches, and moves the selection when a selected stroke is dragged.
 * Pointers it does not take (middle button, space, rejected touches) fall through to the
 * viewport, which pans.
 */
export function DrawOverlay() {
  const active = usePane((s) => s.tool === 'draw');
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <DrawCanvas />;
}

interface InkLive {
  kind: 'ink';
  pointerId: number;
  tool: DrawTool;
  /** World-space points with pressure. */
  points: [number, number, number][];
  realPressure: boolean;
  /** Held still at the end: the line is straight from here on. */
  held: boolean;
  /** Shift was down at the last event: straight, in angle steps. */
  shift: boolean;
  /** Screen point the pointer last settled at; the straighten timer runs from there. */
  anchor: Point;
}

type Live =
  | InkLive
  | { kind: 'erase'; pointerId: number; gesture: EraseGesture }
  | { kind: 'pick'; pointerId: number; /** Screen point. */ at: Point; color: string | null; /** Mode to go back to afterwards. */ returnTo: DrawTool | null }
  | { kind: 'lasso'; pointerId: number; start: Point; /** Flat world coords of the loop. */ ring: number[]; additive: boolean; dragged: boolean }
  | { kind: 'move'; pointerId: number; start: Point; startWorld: Point; hitId: string; additive: boolean; origins: Map<string, Geometry>; dragged: boolean };

function DrawCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Live | null>(null);
  /** Whether the live gesture was started by a finger (a second finger then cancels it). */
  const liveTouch = useRef(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dpr = window.devicePixelRatio || 1;

  // Strokes made before ink layers existed (or left behind by a deleted layer) get their layer now.
  useEffect(() => board.adoptStrokes(), [board]);

  useEffect(
    () => () => {
      clearTimeout(holdTimer.current);
      const l = live.current;
      if (!l) return;
      live.current = null;
      // switched away mid-drag: keep what was erased
      if (l.kind === 'erase') {
        endErase(board, l.gesture);
        pane.getState().setInkPreview(null);
      }
      closeUndoStep(board);
    },
    [board, pane],
  );

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
  }, [size, dpr]);

  // Safari still runs its touch defaults (text selection, callouts, synthesised clicks on the
  // UI underneath) for pointers we ignore. Cancel them at the touch level, non-passively.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const render = () => {
    const c = canvasRef.current;
    const l = live.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    if (!l) return;
    const ts = toolSettings.getState();
    const cam = pane.getState().camera;
    if (l.kind === 'erase') {
      const p = worldToScreen(cam, l.gesture.last);
      ctx.beginPath();
      ctx.arc(p.x, p.y, ts.eraserSize / 2, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(180,40,40,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      return;
    }
    if (l.kind === 'pick') {
      drawLoupe(ctx, c, l.at, l.color, ts.ink[ts.inkTool].color, size);
      return;
    }
    if (l.kind === 'lasso') {
      if (l.ring.length < 4) return;
      const accent = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
      ctx.beginPath();
      for (let i = 0; i < l.ring.length; i += 2) {
        const p = worldToScreen(cam, { x: l.ring[i]!, y: l.ring[i + 1]! });
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.globalAlpha = 0.12;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    if (l.kind !== 'ink' || !l.points.length) return;
    const style = ts.ink[l.tool];
    const straight = isStraight(l);
    const screenPts = inkPoints(l, cam.zoom).map(([x, y, p]) => {
      const s = worldToScreen(cam, { x, y });
      return [s.x, s.y, p] as [number, number, number];
    });
    ctx.fillStyle = style.color;
    if (l.tool === 'fill') {
      if (screenPts.length < 2) return;
      const path = new Path2D(outlineToPath(screenPts));
      ctx.globalAlpha = style.opacity;
      ctx.fill(path);
      // the loop's edge, so a fill in the canvas colour still shows where it goes
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 1;
      ctx.strokeStyle = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
      ctx.setLineDash([4, 3]);
      ctx.stroke(path);
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      return;
    }
    const outline = getStroke(screenPts, strokeOptions(l.tool, style.size, l.realPressure || straight));
    ctx.globalAlpha = (l.tool === 'highlighter' ? HIGHLIGHTER_ALPHA : 1) * style.opacity;
    ctx.fill(new Path2D(outlineToPath(outline)));
    ctx.globalAlpha = 1;
  };

  /** Starts (or restarts) the wait after which a paused line straightens. */
  const armHold = (l: InkLive) => {
    clearTimeout(holdTimer.current);
    if (l.tool === 'fill' || l.held || !toolSettings.getState().holdStraighten) return;
    holdTimer.current = setTimeout(() => {
      if (live.current !== l || !roughlyStraight(l, pane.getState().camera.zoom)) return;
      l.held = true;
      render();
    }, HOLD_MS);
  };

  const pick = (local: Point): string | null => {
    const c = canvasRef.current;
    const viewport = c?.parentElement;
    if (!viewport) return null;
    const cam = pane.getState().camera;
    return sampleColor(board, viewport, screenToWorld(cam, local), cam.zoom);
  };

  /** A selected stroke under the world point, if any (the handle for moving the selection). */
  const selectedStrokeAt = (world: Point, zoom: number): string | null => {
    const r = HIT_PX / zoom;
    const s = pane.getState();
    let best: string | null = null;
    for (const id of s.selection) {
      const item = board.index.get(id);
      if (item && isInk(item) && !board.isLocked(item) && strokeHit(item, world, r)) best = id; // last = topmost in z among selected? close enough
    }
    return best;
  };

  const drop = (target: HTMLCanvasElement) => {
    const l = live.current;
    if (!l) return;
    live.current = null;
    clearTimeout(holdTimer.current);
    if (target.hasPointerCapture(l.pointerId)) target.releasePointerCapture(l.pointerId);
    if (l.kind === 'move') pane.getState().clearTransient();
    if (l.kind === 'erase') pane.getState().setInkPreview(null);
    closeUndoStep(board);
    render();
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (live.current) {
      // A second finger beside a drawing finger makes a gesture (two-finger tap = undo, pinch),
      // not ink: drop the stroke in progress instead of committing a dot the gesture would then
      // undo. The new pointer falls through to the viewport.
      if (e.pointerType === 'touch' && liveTouch.current) drop(e.currentTarget);
      return;
    }
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return; // palm or pen-only: viewport handles (two-finger pan)
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    liveTouch.current = e.pointerType === 'touch';
    const local = toLocal(e);
    const world = screenToWorld(s.camera, local);
    const ts = toolSettings.getState();
    const mode = ts.penMode;
    openUndoStep(board);
    if (mode === 'picker' || e.altKey) {
      live.current = { kind: 'pick', pointerId: e.pointerId, at: local, color: pick(local), returnTo: mode === 'picker' ? ts.inkTool : null };
    } else if (mode === 'eraser') {
      const gesture = startErase(board, ts.eraserWhole, world, ts.eraserSize / 2 / s.camera.zoom);
      live.current = { kind: 'erase', pointerId: e.pointerId, gesture };
      s.setInkPreview(gesture.preview);
    } else if (mode === 'lasso') {
      const additive = e.shiftKey;
      const hitId = selectedStrokeAt(world, s.camera.zoom);
      if (hitId) {
        const origins = new Map<string, Geometry>();
        for (const id of s.selection) {
          const item = board.index.get(id);
          if (item && !board.isLocked(item)) origins.set(id, item);
        }
        live.current = { kind: 'move', pointerId: e.pointerId, start: local, startWorld: world, hitId, additive, origins, dragged: false };
      } else {
        live.current = { kind: 'lasso', pointerId: e.pointerId, start: local, ring: [world.x, world.y], additive, dragged: false };
      }
    } else {
      const realPressure = e.pointerType === 'pen';
      const l: InkLive = {
        kind: 'ink',
        pointerId: e.pointerId,
        tool: mode,
        points: [[world.x, world.y, realPressure ? e.pressure : 0.5]],
        realPressure,
        held: false,
        shift: e.shiftKey,
        anchor: local,
      };
      live.current = l;
      armHold(l);
    }
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    const s = pane.getState();
    if (!l) {
      // idle in lasso mode: show that a selected stroke can be dragged
      if (e.pointerType !== 'touch' && toolSettings.getState().penMode === 'lasso' && s.selection.size) {
        const over = selectedStrokeAt(screenToWorld(s.camera, toLocal(e)), s.camera.zoom);
        e.currentTarget.style.cursor = over ? 'move' : '';
      }
      return;
    }
    if (l.pointerId !== e.pointerId) return;
    const local = toLocal(e);
    if (l.kind === 'move') {
      if (!l.dragged && distance(local, l.start) < TAP_PX) return;
      l.dragged = true;
      const world = screenToWorld(s.camera, local);
      const dx = world.x - l.startWorld.x;
      const dy = world.y - l.startWorld.y;
      const t: Record<string, Partial<Geometry>> = {};
      for (const [id, o] of l.origins) t[id] = { x: o.x + dx, y: o.y + dy };
      s.setTransient(t);
      return;
    }
    if (l.kind === 'pick') {
      l.at = local;
      l.color = pick(local) ?? l.color;
      render();
      return;
    }
    if (l.kind === 'erase') {
      // once per event: the eraser sweeps the whole way from its last position
      if (eraseTo(board, l.gesture, screenToWorld(s.camera, local), toolSettings.getState().eraserSize / 2 / s.camera.zoom)) {
        s.setInkPreview(l.gesture.preview);
      }
      render();
      return;
    }
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    for (const ev of list) {
      const at = toLocal(ev);
      const world = screenToWorld(s.camera, at);
      if (l.kind === 'lasso') {
        if (!l.dragged && distance(at, l.start) >= TAP_PX) l.dragged = true;
        const n = l.ring.length;
        const lastScreen = worldToScreen(s.camera, { x: l.ring[n - 2]!, y: l.ring[n - 1]! });
        if (distance(lastScreen, at) < 2) continue; // keep the loop light: the hit test is O(loop x stroke)
        l.ring.push(world.x, world.y);
      } else {
        const last = l.points[l.points.length - 1]!;
        // fills need far fewer points than a line's outline does
        if (distance({ x: last[0], y: last[1] }, world) * s.camera.zoom < (l.tool === 'fill' ? 1.5 : 0.7)) continue;
        l.points.push([world.x, world.y, l.realPressure ? ev.pressure : 0.5]);
      }
    }
    if (l.kind === 'ink') {
      l.shift = e.shiftKey;
      if (distance(local, l.anchor) > HOLD_SLOP_PX) {
        l.anchor = local;
        armHold(l);
      }
    }
    render();
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l || l.pointerId !== e.pointerId) return;
    live.current = null;
    clearTimeout(holdTimer.current);
    e.currentTarget.releasePointerCapture(e.pointerId);
    render();
    try {
      complete(l, e);
    } finally {
      closeUndoStep(board);
    }
  };

  /** What a gesture leaves behind once the pointer lifts. */
  const complete = (l: Live, e: ReactPointerEvent<HTMLCanvasElement>) => {
    const s = pane.getState();
    const cam = s.camera;
    if (l.kind === 'erase') {
      // the document first, then the preview goes: the strokes never flash back
      endErase(board, l.gesture);
      s.setInkPreview(null);
      return;
    }
    if (l.kind === 'pick') {
      const color = e.type === 'pointercancel' ? null : (pick(toLocal(e)) ?? l.color);
      const { inkTool } = toolSettings.getState();
      if (color) setInkStyle(inkTool, { color });
      if (l.returnTo) setPenMode(l.returnTo);
      return;
    }
    if (l.kind === 'move') {
      if (l.dragged) {
        commitTransient({ board, pane });
      } else if (l.additive) {
        s.select([...s.selection].filter((id) => id !== l.hitId));
      } else {
        s.select([l.hitId]);
      }
      return;
    }
    if (l.kind === 'lasso') {
      const ctx = { board, pane };
      if (!l.dragged) {
        // a tap: pick the stroke under it, or clear
        const hit = strokeAt(board, screenToWorld(cam, toLocal(e)), HIT_PX / cam.zoom);
        const members = hit ? filterSelectable(board, expandGroups(ctx, [hit.id]), 'draw') : [];
        if (l.additive) s.addToSelection(members);
        else s.select(members);
        return;
      }
      const hits = filterSelectable(board, expandGroups(ctx, lassoStrokes(board, l.ring)), 'draw');
      if (l.additive) s.addToSelection(hits);
      else s.select(hits);
      return;
    }
    const style = toolSettings.getState().ink[l.tool];
    const fill = l.tool === 'fill';
    const straight = isStraight(l);
    const pts = inkPoints(l, cam.zoom);
    // a fill needs an area: a click or a scribble along one line paints nothing
    if (fill && (pts.length < 3 || Math.abs(polygonArea(pts)) * cam.zoom * cam.zoom < 16)) return;
    const sizeWorld = fill ? 0 : style.size / cam.zoom;
    const pad = sizeWorld;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of pts) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const x = minX - pad;
    const y = minY - pad;
    const w = Math.max(1, maxX - minX + pad * 2);
    const h = Math.max(1, maxY - minY + pad * 2);
    const flat: number[] = [];
    for (const [px, py, p] of pts) flat.push(round(px - x), round(py - y), round(p));
    board.createItem({
      type: 'stroke',
      x: round(x),
      y: round(y),
      w: round(w),
      h: round(h),
      props: {
        tool: l.tool,
        color: style.color,
        size: round(sizeWorld),
        points: flat,
        w0: round(w),
        h0: round(h),
        // a straight line carries an even pressure, so pens draw it at an even width
        pressure: l.realPressure || straight,
        ...(style.opacity < 1 ? { opacity: round(style.opacity) } : {}),
      },
    });
  };

  return (
    <canvas
      ref={canvasRef}
      className="draw-overlay"
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    />
  );
}

const round = (v: number) => Math.round(v * 100) / 100;

const isStraight = (l: InkLive): boolean => l.tool !== 'fill' && (l.held || l.shift);

/** The points the stroke will be made of: as drawn, or a straight line from its start. */
function inkPoints(l: InkLive, zoom: number): [number, number, number][] {
  if (!isStraight(l) || l.points.length < 2) return l.points;
  const [x0, y0] = l.points[0]!;
  let [x1, y1] = l.points[l.points.length - 1]!;
  const len = Math.hypot(x1 - x0, y1 - y0);
  if (l.shift) {
    const a = Math.round(Math.atan2(y1 - y0, x1 - x0) / ANGLE_STEP) * ANGLE_STEP;
    x1 = x0 + Math.cos(a) * len;
    y1 = y0 + Math.sin(a) * len;
  }
  // evenly spaced points (every 2 screen px, at most 256), so tapers and cuts have something to work with
  const n = Math.max(1, Math.min(256, Math.ceil((len * zoom) / 2)));
  const out: [number, number, number][] = [];
  for (let i = 0; i <= n; i++) out.push([x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n, 0.5]);
  return out;
}

/** Is the line so far close enough to its chord to be meant as straight (not a curve)? */
function roughlyStraight(l: InkLive, zoom: number): boolean {
  const pts = l.points;
  if (pts.length < 2) return false;
  const [x0, y0] = pts[0]!;
  const [x1, y1] = pts[pts.length - 1]!;
  const chord = Math.hypot(x1 - x0, y1 - y0) * zoom;
  if (chord < MIN_LINE_PX) return false;
  const tolerance = Math.max(4, chord * 0.12);
  for (const [x, y] of pts) {
    const off = (Math.abs((x1 - x0) * (y0 - y) - (x0 - x) * (y1 - y0)) / Math.hypot(x1 - x0, y1 - y0)) * zoom;
    if (off > tolerance) return false;
  }
  return true;
}

/** Signed area of a polygon given as [x, y, ...] tuples (shoelace). */
function polygonArea(pts: [number, number, number][]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j]![0] - pts[i]![0]) * (pts[j]![1] + pts[i]![1]);
  return a / 2;
}

/**
 * The picker's swatch beside the pointer: the colour under it on top, the current one below,
 * and a small square marking the sampled spot.
 */
function drawLoupe(ctx: CanvasRenderingContext2D, c: HTMLElement, at: Point, picked: string | null, current: string, view: { w: number; h: number }): void {
  const S = 44;
  const GAP = 16;
  const css = getComputedStyle(c);
  const line = css.getPropertyValue('--line').trim() || '#111111';
  let x = at.x - S - GAP;
  let y = at.y - S - GAP;
  if (x < 4) x = Math.min(at.x + GAP, view.w - S - 4);
  if (y < 4) y = Math.min(at.y + GAP, view.h - S - 4);
  ctx.fillStyle = line;
  ctx.fillRect(x + 2, y + 2, S + 2, S + 2);
  ctx.fillStyle = picked ?? current;
  ctx.fillRect(x - 1, y - 1, S + 2, S / 2 + 1);
  ctx.fillStyle = current;
  ctx.fillRect(x - 1, y + S / 2, S + 2, S / 2 + 1);
  ctx.lineWidth = 2;
  ctx.strokeStyle = line;
  ctx.strokeRect(x - 1, y - 1, S + 2, S + 2);
  // the sampled spot: a light and a dark square, so it shows on any colour
  ctx.lineWidth = 1;
  ctx.strokeStyle = '#ffffff';
  ctx.strokeRect(at.x - 4.5, at.y - 4.5, 9, 9);
  ctx.strokeStyle = '#000000';
  ctx.strokeRect(at.x - 5.5, at.y - 5.5, 11, 11);
}
